import { Storage } from '@capacitor/storage';
import {
  AppGradientConfig,
  AppConfig,
  DeepPartial,
  applyRemoteAppConfig,
  getAppConfig
} from '../config/app.config';
import {
  FirebaseWebConfig,
  setRuntimeFirebaseConfig
} from '../config/runtime.config';

const parseHexColor = (color: string): [number, number, number] | null => {
  const normalized = color.trim().replace(/^#/, '');
  const expanded = normalized.length === 3
    ? normalized.split('').map(character => character + character).join('')
    : normalized;

  if (!/^[0-9a-f]{6}$/i.test(expanded)) return null;
  return [
    parseInt(expanded.slice(0, 2), 16),
    parseInt(expanded.slice(2, 4), 16),
    parseInt(expanded.slice(4, 6), 16)
  ];
};

const rgbChannels = (color: string): string | null => {
  const rgb = parseHexColor(color);
  return rgb ? rgb.join(', ') : null;
};

const mixHexColor = (color: string, target: number, amount: number): string => {
  const rgb = parseHexColor(color);
  if (!rgb) return color;

  const mixed = rgb.map(channel => Math.round(channel + (target - channel) * amount));
  return `#${mixed.map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
};

const gradientValue = (gradient: AppGradientConfig): string => (
  `linear-gradient(${gradient.angle}deg, ${gradient.start}, ${gradient.end})`
);

export interface AppBootstrapPayload {
  schemaVersion: number;
  version: string;
  clientId: string;
  profile: string;
  firebase: FirebaseWebConfig;
  app: DeepPartial<AppConfig>;
}

export interface AppRuntimeConfiguration {
  schemaVersion: number;
  profile: string;
  configEndpoint: string;
}

export interface AppBootstrapResult {
  source: 'remote' | 'cache' | 'legacy';
  payload: AppBootstrapPayload | null;
  config: AppConfig;
}

export class AppBootstrapService {
  private static instance: AppBootstrapService;
  private static readonly CACHE_KEY = 'appBootstrapConfig';
  private static readonly ETAG_KEY = 'appBootstrapEtag';
  private static readonly RUNTIME_CACHE_KEY = 'appRuntimeConfig';
  private currentPayload: AppBootstrapPayload | null = null;
  private manifestObjectUrl: string | null = null;

  static getInstance(): AppBootstrapService {
    if (!AppBootstrapService.instance) {
      AppBootstrapService.instance = new AppBootstrapService();
    }
    return AppBootstrapService.instance;
  }

  async initialize(): Promise<AppBootstrapResult> {
    const runtimeConfiguration = await this.resolveRuntimeConfiguration();
    const expectedProfile = runtimeConfiguration?.profile || '';
    const cached = await this.readCachedPayload(expectedProfile);

    try {
      const remote = await this.fetchRemotePayload(
        cached,
        this.getEndpoint(runtimeConfiguration),
        expectedProfile
      );
      if (remote) {
        await Storage.set({
          key: this.cacheKey(AppBootstrapService.CACHE_KEY, expectedProfile),
          value: JSON.stringify(remote.payload)
        });
        if (remote.etag) {
          await Storage.set({
            key: this.cacheKey(AppBootstrapService.ETAG_KEY, expectedProfile),
            value: remote.etag
          });
        }
        return this.applyPayload(remote.payload, 'remote');
      }

      if (cached) {
        return this.applyPayload(cached, 'cache');
      }
    } catch (error) {
      console.warn('AppBootstrapService: No se pudo actualizar el perfil remoto.', error);
      if (cached) {
        return this.applyPayload(cached, 'cache');
      }

      if (runtimeConfiguration) {
        throw error;
      }
    }

    this.applyBranding(getAppConfig());
    return {
      source: 'legacy',
      payload: null,
      config: getAppConfig()
    };
  }

  getCurrentPayload(): AppBootstrapPayload | null {
    return this.currentPayload;
  }

  private async fetchRemotePayload(
    cached: AppBootstrapPayload | null,
    endpoint: string,
    expectedProfile: string
  ): Promise<{
    payload: AppBootstrapPayload;
    etag: string;
  } | null> {
    const storedEtag = (
      await Storage.get({
        key: this.cacheKey(AppBootstrapService.ETAG_KEY, expectedProfile || cached?.profile || '')
      })
    ).value;
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (storedEtag && cached) headers['If-None-Match'] = storedEtag;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    let response: Response;

    try {
      response = await fetch(endpoint, {
        method: 'GET',
        headers,
        cache: 'no-cache',
        signal: controller.signal
      });
    } finally {
      clearTimeout(timeout);
    }

    if (response.status === 304 && cached) return null;
    if (!response.ok) {
      throw new Error(`APP_CONFIG_HTTP_${response.status}`);
    }

    const payload = await response.json();
    if (!this.isValidPayload(payload)) {
      throw new Error('APP_CONFIG_INVALID');
    }
    if (expectedProfile && payload.profile !== expectedProfile) {
      throw new Error('APP_CONFIG_PROFILE_MISMATCH');
    }

    return {
      payload,
      etag: response.headers.get('ETag') || ''
    };
  }

  private getEndpoint(runtimeConfiguration: AppRuntimeConfiguration | null): string {
    if (runtimeConfiguration) {
      const endpoint = new URL(runtimeConfiguration.configEndpoint, window.location.href);
      endpoint.searchParams.set('profile', runtimeConfiguration.profile);
      return endpoint.toString();
    }

    if (process.env.REACT_APP_APP_CONFIG_URL) {
      return process.env.REACT_APP_APP_CONFIG_URL;
    }

    const authBaseUrl = (
      process.env.REACT_APP_AUTH_API_BASE_URL
      || 'https://esimposio.com/miesimposio/app_login'
    ).replace(/\/$/, '');

    return `${authBaseUrl}/app_config.php`;
  }

  private async resolveRuntimeConfiguration(): Promise<AppRuntimeConfiguration | null> {
    const runtimeUrl = this.getRuntimeConfigurationUrl();

    try {
      const response = await fetch(runtimeUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(`APP_RUNTIME_CONFIG_HTTP_${response.status}`);
      const contentType = response.headers?.get('Content-Type') || '';
      if (contentType.toLowerCase().includes('text/html')) return null;

      const value = await response.json();
      if (!this.isValidRuntimeConfiguration(value)) {
        throw new Error('APP_RUNTIME_CONFIG_INVALID');
      }
      await Storage.set({
        key: AppBootstrapService.RUNTIME_CACHE_KEY,
        value: JSON.stringify(value)
      });
      return value;
    } catch (error) {
      const cached = await this.readCachedRuntimeConfiguration();
      if (cached) {
        console.warn('AppBootstrapService: Se usará el descriptor de despliegue cacheado.', error);
        return cached;
      }
      throw error;
    }
  }

  private getRuntimeConfigurationUrl(): string {
    const configuredUrl = process.env.REACT_APP_RUNTIME_CONFIG_URL;
    if (configuredUrl) return new URL(configuredUrl, window.location.href).toString();

    const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    return new URL(`${publicBase}/app-runtime.json`, window.location.href).toString();
  }

  private async readCachedRuntimeConfiguration(): Promise<AppRuntimeConfiguration | null> {
    try {
      const cached = await Storage.get({ key: AppBootstrapService.RUNTIME_CACHE_KEY });
      if (!cached.value) return null;
      const value = JSON.parse(cached.value);
      return this.isValidRuntimeConfiguration(value) ? value : null;
    } catch (error) {
      console.warn('AppBootstrapService: Se ignoró un descriptor de despliegue inválido.', error);
      return null;
    }
  }

  private isValidRuntimeConfiguration(value: unknown): value is AppRuntimeConfiguration {
    if (!value || typeof value !== 'object') return false;
    const configuration = value as Partial<AppRuntimeConfiguration>;
    if (configuration.schemaVersion !== 1) return false;
    if (typeof configuration.profile !== 'string'
      || !/^[a-z0-9][a-z0-9-]{0,63}$/.test(configuration.profile)) return false;
    if (typeof configuration.configEndpoint !== 'string' || !configuration.configEndpoint.trim()) return false;

    try {
      const endpoint = new URL(configuration.configEndpoint, window.location.href);
      return endpoint.protocol === 'https:'
        || (endpoint.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(endpoint.hostname));
    } catch (error) {
      return false;
    }
  }

  private cacheKey(base: string, profile: string): string {
    return profile ? `${base}:${profile}` : base;
  }

  private async readCachedPayload(profile: string): Promise<AppBootstrapPayload | null> {
    try {
      const scopedKey = this.cacheKey(AppBootstrapService.CACHE_KEY, profile);
      const cached = await Storage.get({ key: scopedKey });
      if (!cached.value) return null;

      const payload = JSON.parse(cached.value);
      return this.isValidPayload(payload) && (!profile || payload.profile === profile)
        ? payload
        : null;
    } catch (error) {
      console.warn('AppBootstrapService: Se ignoró una configuración cacheada inválida.', error);
      return null;
    }
  }

  private isValidPayload(value: unknown): value is AppBootstrapPayload {
    if (!value || typeof value !== 'object') return false;
    const payload = value as Partial<AppBootstrapPayload>;
    const firebase = payload.firebase as Partial<FirebaseWebConfig> | undefined;

    return payload.schemaVersion === 1
      && typeof payload.version === 'string'
      && payload.version.length > 0
      && typeof payload.clientId === 'string'
      && payload.clientId.length > 0
      && typeof payload.profile === 'string'
      && payload.profile.length > 0
      && Boolean(payload.app && typeof payload.app === 'object')
      && Boolean(firebase)
      && typeof firebase?.apiKey === 'string'
      && firebase.apiKey.length > 0
      && typeof firebase.authDomain === 'string'
      && firebase.authDomain.length > 0
      && typeof firebase.databaseURL === 'string'
      && firebase.databaseURL.length > 0
      && typeof firebase.projectId === 'string'
      && firebase.projectId.length > 0;
  }

  private applyPayload(
    payload: AppBootstrapPayload,
    source: 'remote' | 'cache'
  ): AppBootstrapResult {
    payload.app.auth = {
      ...(payload.app.auth || {}),
      client: payload.clientId
    };

    const config = applyRemoteAppConfig(payload.app);
    setRuntimeFirebaseConfig(payload.firebase);
    this.applyBranding(config);
    this.currentPayload = payload;

    console.log(`AppBootstrapService: Perfil ${payload.profile} ${payload.version} cargado desde ${source}.`);
    return { source, payload, config };
  }

  private applyBranding(config: AppConfig): void {
    if (typeof document === 'undefined') return;

    const { branding } = config;
    const { colors, gradients } = branding;
    const root = document.documentElement;
    const extendedColors = {
      modalBackground: colors.modalBackground || colors.surface,
      dark: colors.dark || colors.textDark,
      medium: colors.medium || colors.textSecondary,
      light: colors.light || colors.surface
    };
    const variables: Record<string, string> = {
      '--app-primary-color': colors.primary,
      '--primary': colors.uiPrimary || colors.primary,
      '--app-secondary-color': colors.secondary,
      '--secondary': colors.uiSecondary || colors.secondary,
      '--app-tertiary-color': colors.tertiary,
      '--app-accent-color': colors.accent,
      '--accent': colors.uiAccent || colors.accent,
      '--app-success-color': colors.success,
      '--app-warning-color': colors.warning,
      '--app-danger-color': colors.danger,
      '--app-favorite-color': colors.favorite,
      '--app-background': colors.background,
      '--app-surface-background': colors.surface,
      '--app-card-bg': colors.cardBackground,
      '--app-card-background': colors.cardBackground,
      '--app-modal-background': extendedColors.modalBackground,
      '--app-text-light': colors.textLight,
      '--app-text-dark': colors.textDark,
      '--app-text-primary': colors.textDark,
      '--app-text-secondary': colors.textSecondary,
      '--app-text-tertiary': colors.border,
      '--app-text-inverse': colors.textLight,
      '--app-border-color': colors.border,
      '--app-primary-gradient': gradientValue(gradients.primary),
      '--app-secondary-gradient': gradientValue(gradients.secondary),
      '--app-accent-gradient': gradientValue(gradients.accent),
      '--app-background-gradient': gradientValue(gradients.background),
      '--app-navigation-gradient': gradientValue(gradients.navigation),
      '--app-tab-bar-gradient': gradientValue(gradients.tabBar),
      '--app-header-gradient': gradientValue(gradients.header),
      '--app-card-gradient': gradientValue(gradients.card),
      '--app-button-gradient': gradientValue(gradients.button),
      '--app-success-gradient': gradientValue(gradients.success),
      '--app-bg-gradient': gradientValue(gradients.background),
      '--gradient-primary': gradientValue(gradients.primary),
      '--gradient-secondary': gradientValue(gradients.secondary),
      '--gradient-accent': gradientValue(gradients.accent),
      '--gradient-angle': `${gradients.background.angle}deg`,
      '--gradient-bg-start': gradients.background.start,
      '--gradient-bg-end': gradients.background.end,
      '--ion-background-color': colors.background,
      '--ion-text-color': colors.textDark,
      '--ion-color-navigation': colors.primary
    };

    const optionalColors: Array<[string, string | undefined]> = [
      ['--primary-light', colors.primaryLight],
      ['--primary-dark', colors.primaryDark],
      ['--neutral-50', colors.neutral50],
      ['--neutral-100', colors.neutral100],
      ['--neutral-200', colors.neutral200],
      ['--neutral-300', colors.neutral300],
      ['--neutral-400', colors.neutral400],
      ['--neutral-500', colors.neutral500],
      ['--neutral-600', colors.neutral600],
      ['--neutral-700', colors.neutral700],
      ['--neutral-800', colors.neutral800],
      ['--neutral-900', colors.neutral900],
      ['--app-day-selector-text', colors.daySelectorText],
      ['--app-day-selector-selected-background', colors.daySelectorSelectedBackground],
      ['--app-day-selector-selected-text', colors.daySelectorSelectedText],
      ['--app-day-selector-indicator', colors.daySelectorIndicator],
      ['--app-day-selector-dark-indicator', colors.daySelectorDarkIndicator],
      ['--app-session-card-background', colors.sessionCardBackground],
      ['--app-session-card-text', colors.sessionCardText],
      ['--app-session-card-secondary-text', colors.sessionCardSecondaryText],
      ['--app-event-info-accent', colors.eventInfoAccent],
      ['--app-event-info-accent-text', colors.eventInfoAccentText],
      ['--app-selected-tab-background', colors.selectedTabBackground],
      ['--app-floating-menu-background', colors.floatingMenuBackground],
      ['--app-floating-menu-dark-background', colors.floatingMenuDarkBackground]
    ];
    optionalColors.forEach(([name, value]) => {
      if (value) variables[name] = value;
      else root.style.removeProperty(name);
    });

    const optionalGradients: Array<[string, AppGradientConfig | undefined]> = [
      ['--app-blue-gradient', gradients.blue],
      ['--app-green-gradient', gradients.green],
      ['--app-warm-gradient', gradients.warm],
      ['--app-cool-gradient', gradients.cool],
      ['--app-sunset-gradient', gradients.sunset],
      ['--app-ocean-gradient', gradients.ocean],
      ['--app-day-selector-background', gradients.daySelector]
    ];
    optionalGradients.forEach(([name, value]) => {
      if (value) variables[name] = gradientValue(value);
      else root.style.removeProperty(name);
    });

    const backgroundRgb = rgbChannels(colors.background);
    const textRgb = rgbChannels(colors.textDark);
    const borderRgb = rgbChannels(colors.border);
    if (backgroundRgb) variables['--ion-background-color-rgb'] = backgroundRgb;
    if (textRgb) variables['--ion-text-color-rgb'] = textRgb;
    if (borderRgb) variables['--app-text-tertiary-rgb'] = borderRgb;

    Object.entries(variables).forEach(([name, value]) => {
      if (value) root.style.setProperty(name, value);
    });

    this.applyIonicColor(root, 'primary', colors.primary, colors.textLight);
    this.applyIonicColor(root, 'secondary', colors.secondary, colors.textLight);
    this.applyIonicColor(root, 'tertiary', colors.tertiary, colors.textLight);
    this.applyIonicColor(root, 'success', colors.success, colors.textLight);
    this.applyIonicColor(root, 'warning', colors.warning, colors.textDark);
    this.applyIonicColor(root, 'danger', colors.danger, colors.textLight);
    this.applyIonicColor(root, 'favorite', colors.favorite, colors.textLight);
    this.applyIonicColor(root, 'dark', extendedColors.dark, colors.textLight);
    this.applyIonicColor(root, 'medium', extendedColors.medium, colors.textLight);
    this.applyIonicColor(root, 'light', extendedColors.light, colors.textDark);
    this.applyOptionalIonicColor(root, 'twitter', colors.twitter, colors.textLight);
    this.applyOptionalIonicColor(root, 'google', colors.google, colors.textLight);
    this.applyOptionalIonicColor(root, 'vimeo', colors.vimeo, colors.textLight);
    this.applyOptionalIonicColor(root, 'facebook', colors.facebook, colors.textLight);
    this.applyOptionalIonicColor(root, 'instagram', colors.instagram, colors.textLight);

    if (branding.name) document.title = branding.name;
    if (branding.appIcon) {
      const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (favicon) favicon.href = branding.appIcon;
    }
    this.applyWebAppMetadata(config);
  }

  private applyOptionalIonicColor(
    root: HTMLElement,
    name: string,
    color: string | undefined,
    contrast: string
  ): void {
    if (color) {
      this.applyIonicColor(root, name, color, contrast);
      return;
    }

    ['', '-rgb', '-contrast', '-contrast-rgb', '-shade', '-tint'].forEach(suffix => {
      root.style.removeProperty(`--ion-color-${name}${suffix}`);
    });
  }

  private applyWebAppMetadata(config: AppConfig): void {
    const { branding } = config;
    const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (themeColor) themeColor.content = branding.colors.primary;

    const appleTitle = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    if (appleTitle) appleTitle.content = branding.shortName || branding.name;

    if (branding.appIcon) {
      let appleIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
      if (!appleIcon) {
        appleIcon = document.createElement('link');
        appleIcon.rel = 'apple-touch-icon';
        document.head.appendChild(appleIcon);
      }
      appleIcon.href = branding.appIcon;
    }

    const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!manifestLink) return;

    const publicBase = (process.env.PUBLIC_URL || '/').replace(/\/?$/, '/');
    const fallbackIcon = new URL(`${publicBase}assets/icon/favicon.png`, window.location.href).toString();
    const icon = branding.appIcon
      ? new URL(branding.appIcon, window.location.href).toString()
      : fallbackIcon;
    const iconType = icon.toLowerCase().includes('.svg') ? 'image/svg+xml' : 'image/png';
    const manifest = {
      name: branding.name,
      short_name: branding.shortName || branding.name,
      icons: [{
        src: icon,
        sizes: iconType === 'image/svg+xml' ? 'any' : '512x512',
        type: iconType,
        purpose: 'any maskable'
      }],
      start_url: publicBase,
      scope: publicBase,
      display: 'standalone',
      theme_color: branding.colors.primary,
      background_color: branding.colors.background
    };
    const serializedManifest = JSON.stringify(manifest);

    if (this.manifestObjectUrl && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(this.manifestObjectUrl);
      this.manifestObjectUrl = null;
    }
    if (typeof URL.createObjectURL === 'function') {
      this.manifestObjectUrl = URL.createObjectURL(
        new Blob([serializedManifest], { type: 'application/manifest+json' })
      );
      manifestLink.href = this.manifestObjectUrl;
    } else {
      manifestLink.href = `data:application/manifest+json,${encodeURIComponent(serializedManifest)}`;
    }
  }

  private applyIonicColor(
    root: HTMLElement,
    name: string,
    base: string,
    contrast: string
  ): void {
    const baseRgb = rgbChannels(base);
    const contrastRgb = rgbChannels(contrast);
    const prefix = `--ion-color-${name}`;

    root.style.setProperty(prefix, base);
    root.style.setProperty(`${prefix}-contrast`, contrast);
    root.style.setProperty(`${prefix}-shade`, mixHexColor(base, 0, 0.12));
    root.style.setProperty(`${prefix}-tint`, mixHexColor(base, 255, 0.10));
    if (baseRgb) root.style.setProperty(`${prefix}-rgb`, baseRgb);
    if (contrastRgb) root.style.setProperty(`${prefix}-contrast-rgb`, contrastRgb);
  }
}

export const appBootstrapService = AppBootstrapService.getInstance();
